/**
 * HeartbeatLabel — F-005 Phase 4 (IMPL-P4-001).
 *
 * Promoted from `features/operatore/components/HeartbeatHeader.tsx` so that
 * both the operator dashboard (F-007) and the client dashboard (F-005) can
 * share the same "Aggiornato Xs fa" timer.
 *
 * Mostra un label aggiornato ogni secondo basato su `dataUpdatedAt` (di
 * norma `query.dataUpdatedAt` di TanStack Query). Se 0 o non definito,
 * mostra un placeholder "In attesa di aggiornamento…".
 *
 * Accessibilità:
 *   - `role="status"` + `aria-live="polite"` annunciano il timer agli SR
 *     senza interrompere il flusso (refresh periodico).
 */
import * as React from 'react';

export interface HeartbeatLabelProps {
  /**
   * Timestamp epoch (ms) del momento in cui i dati sono stati ricevuti
   * dal server — di norma `query.dataUpdatedAt` di TanStack Query.
   * Se 0 (mai aggiornato), il componente mostra un placeholder.
   */
  dataUpdatedAt: number;
  className?: string;
}

export function HeartbeatLabel({ dataUpdatedAt, className }: HeartbeatLabelProps) {
  const [label, setLabel] = React.useState<string>('');

  React.useEffect(() => {
    if (!dataUpdatedAt) {
      setLabel('In attesa di aggiornamento…');
      return;
    }
    const update = () => {
      const seconds = Math.max(0, Math.round((Date.now() - dataUpdatedAt) / 1000));
      setLabel(`Aggiornato ${seconds}s fa`);
    };
    update();
    const id = setInterval(update, 1_000);
    return () => clearInterval(id);
  }, [dataUpdatedAt]);

  return (
    <p
      role="status"
      aria-live="polite"
      className={
        'text-sm text-muted-foreground' + (className ? ` ${className}` : '')
      }
    >
      {label}
    </p>
  );
}
